import axiosInstance from '../../services/axiosInstance';
import {
  ValidooPrepareBlockedResponse,
  ValidooPrepareSuccessResponse,
} from './types';

function getFilenameFromDisposition(disposition?: string, fallback = 'download'): string {
  if (!disposition) return fallback;
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function formatBytes(sizeBytes: number): string {
  if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return 'okänd storlek';
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function buildValidooExportSummaryText(result: ValidooPrepareSuccessResponse): string {
  const passedChecks = (result.preflight?.checks || []).filter((check) => check.status === 'pass').length;
  const failedChecks = (result.preflight?.checks || []).filter((check) => check.status !== 'pass').length;
  const now = new Date();
  const originalFileName = result.preflight?.asset?.fileName || 'okänd';
  const iccName = result.preflight?.resolvedData?.icc?.sourceProfileName || 'okänd';
  const colorSpace = result.preflight?.resolvedData?.icc?.sourceColorSpace || 'okänd';
  const clippingPathName = result.preflight?.resolvedData?.clippingPath?.name || 'okänd';

  return [
    'Validoo-export',
    '',
    `Status: Godkänd`,
    `Exporterat: ${now.toLocaleString('sv-SE')}`,
    `GTIN: ${result.gtin}`,
    `Asset-ID: ${result.assetId}`,
    `Originalfil: ${originalFileName}`,
    '',
    'Filer',
    `- Marknadsbild: ${result.downloads.marketImage.filename} (${formatBytes(result.downloads.marketImage.sizeBytes)})`,
    `- Planogrambild: ${result.downloads.planogramImage.filename} (${formatBytes(result.downloads.planogramImage.sizeBytes)})`,
    '',
    'Verifierat',
    '- Marknadsbilden är godkänd som original JPG för Validoo.',
    '- Källbilden är verifierad som RGB.',
    '- Planogrambilden är genererad som PNG med transparent bakgrund.',
    '- Planogrambilden är verifierad med Adobe RGB (1998).',
    '- Planogrammet är verifierat till 500 px längsta sida och 300 DPI.',
    `- Källans ICC-profil: ${iccName}`,
    `- Källans färgrymd: ${colorSpace}`,
    `- Källans urklippsbana: ${clippingPathName}`,
    '',
    'Preflight',
    `- Godkända kontroller: ${passedChecks}`,
    `- Övriga kontroller: ${failedChecks}`,
  ].join('\n');
}

export async function prepareValidooExport(assetId: string): Promise<ValidooPrepareSuccessResponse> {
  const response = await axiosInstance.post(`/api/validoo-export/${assetId}/prepare`, {});
  return response.data as ValidooPrepareSuccessResponse;
}

export async function downloadPreparedValidooFile(
  downloadUrl: string,
  fallbackFilename: string
): Promise<void> {
  const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const disposition = response.headers['content-disposition'] || '';
  const filename = getFilenameFromDisposition(disposition, fallbackFilename);
  const blob = new Blob([response.data], { type: contentType });
  triggerBrowserDownload(blob, filename);
}

export function downloadValidooExportSummary(result: ValidooPrepareSuccessResponse): void {
  const summaryText = buildValidooExportSummaryText(result);
  const filename = `${result.gtin}_validoo-exportinfo.txt`;
  const blob = new Blob([summaryText], { type: 'text/plain;charset=utf-8' });
  triggerBrowserDownload(blob, filename);
}

export function isValidooBlockedResponse(error: any): error is { response: { status: 422; data: ValidooPrepareBlockedResponse } } {
  return Boolean(error?.response?.status === 422 && error?.response?.data?.preflight);
}
